import React, { useState, useEffect, useRef } from "react";
import { ESTADOS } from "../constants";
import { estadoActual, fmtDatetime, tareasSorted, contadores, parseDocx } from "../utils";

function TaskBadge({ estado }) {
  const e = ESTADOS[estado] || ESTADOS["Planificada"];
  return (
    <span 
      className="badge"
      style={{ background: e.bg, color: e.color, borderColor: `${e.color}33`, borderWidth: "1px", borderStyle: "solid" }}
    >
      {e.label}
    </span>
  );
}

function PriorityBadge({ prioridad }) {
  if (!prioridad || prioridad === "normal") return null;
  
  const configs = {
    critica: { label: "CRÍTICA", bg: "#fde8e8", color: "#9b1c1c" },
    alta: { label: "ALTA", bg: "#fef3c7", color: "#d97706" },
    baja: { label: "BAJA", bg: "#ecfdf5", color: "#059669" }
  };
  
  const c = configs[prioridad];
  if (!c) return null;
  
  return (
    <span 
      className="badge"
      style={{ 
        background: c.bg, 
        color: c.color, 
        borderColor: `${c.color}22`,
        borderWidth: "1px",
        borderStyle: "solid",
        fontSize: "10.5px", 
        fontWeight: "600",
        textTransform: "uppercase"
      }}
    >
      {c.label}
    </span>
  );
}

function TaskCard({ 
  tarea, 
  expanded, 
  onToggleExpand, 
  onDelete, 
  userName, 
  onAddAvance, 
  avanceInputs, 
  avanceEstados, 
  onInputChange, 
  onEstadoChange, 
  saving, 
  onEdit,
  tareas = [],
  onEditAvance,
  onDeleteAvance,
  onMoveAvance
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitulo, setEditTitulo] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editResponsable, setEditResponsable] = useState("");
  const [editProyecto, setEditProyecto] = useState("");
  const [editJira, setEditJira] = useState("");
  const [editPrioridad, setEditPrioridad] = useState("normal");

  const [editingAvanceId, setEditingAvanceId] = useState(null);
  const [editAvanceTexto, setEditAvanceTexto] = useState("");
  const [editAvanceEstado, setEditAvanceEstado] = useState("");
  const [editAvanceAutor, setEditAvanceAutor] = useState("");
  const [movingAvanceId, setMovingAvanceId] = useState(null);

  const handleStartEditAvance = (av) => {
    setEditingAvanceId(av.id);
    setEditAvanceTexto(av.texto || "");
    setEditAvanceEstado(av.estado || "Avanzando");
    setEditAvanceAutor(av.autor || "");
  };

  const est = estadoActual(tarea);
  const e = ESTADOS[est] || ESTADOS["Planificada"];

  if (isEditing) {
    return (
      <div className="form-panel" style={{ margin: "8px 0" }}>
        <div className="form-title">Editar Tarea</div>
        <div className="grid2">
          <div>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Título *</label>
            <input 
              type="text" 
              placeholder="Título *" 
              value={editTitulo} 
              onChange={(e) => setEditTitulo(e.target.value)} 
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Responsable *</label>
            <input 
              type="text" 
              placeholder="Responsable *" 
              value={editResponsable} 
              onChange={(e) => setEditResponsable(e.target.value)} 
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Proyecto</label>
            <input 
              type="text" 
              placeholder="Proyecto" 
              value={editProyecto} 
              onChange={(e) => setEditProyecto(e.target.value)} 
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Clave Jira (opcional)</label>
            <input 
              type="text" 
              placeholder="Clave Jira (ej: DGIT-123)" 
              value={editJira} 
              onChange={(e) => setEditJira(e.target.value)} 
            />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Descripción</label>
            <textarea 
              placeholder="Descripción detallada de la tarea..." 
              value={editDescripcion} 
              onChange={(e) => setEditDescripcion(e.target.value)} 
              rows="3"
            />
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
            <span style={{ fontSize: "13.5px", color: "var(--text-muted)" }}>Prioridad:</span>
            <select 
              value={editPrioridad}
              onChange={(e) => setEditPrioridad(e.target.value)}
              style={{ width: "auto", padding: "8px 12px" }}
            >
              <option value="critica">Crítica</option>
              <option value="alta">Alta</option>
              <option value="normal">Normal</option>
              <option value="baja">Baja</option>
            </select>
          </div>
        </div>
        <div className="row-gap" style={{ marginTop: "14px" }}>
          <button 
            className="btn-success" 
            disabled={!editTitulo.trim() || !editResponsable.trim() || saving}
            onClick={async () => {
              const success = await onEdit(tarea.id, {
                titulo: editTitulo.trim(),
                descripcion: editDescripcion.trim(),
                responsable: editResponsable.trim(),
                proyecto: editProyecto.trim(),
                jira: editJira.trim(),
                prioridad: editPrioridad
              });
              if (success) setIsEditing(false);
            }}
          >
            <i className="ti ti-check"></i> Guardar
          </button>
          <button 
            onClick={() => setIsEditing(false)} 
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ borderLeft: `4px solid ${e.color}` }}>
      {/* Cabecera */}
      <div 
        className="card-header-clickable"
        onClick={() => onToggleExpand(tarea.id)}
      >
        <i className={`ti ti-chevron-${expanded ? "up" : "down"}`} style={{ color: "var(--text-light)", fontSize: "16px" }}></i>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
            <span 
              style={{ 
                fontWeight: "600", 
                fontSize: "15.5px", 
                color: "var(--text-dark)",
                cursor: "pointer",
                transition: "color 0.2s"
              }}
              title="Hacé clic para editar la tarea"
              onClick={(e) => { 
                e.stopPropagation(); 
                setIsEditing(true); 
                setEditTitulo(tarea.titulo);
                setEditDescripcion(tarea.descripcion || "");
                setEditResponsable(tarea.responsable || "");
                setEditProyecto(tarea.proyecto || "");
                setEditJira(tarea.jira || "");
                setEditPrioridad(tarea.prioridad || "normal");
              }}
              onMouseOver={(e) => e.currentTarget.style.color = "var(--accent)"}
              onMouseOut={(e) => e.currentTarget.style.color = "var(--text-dark)"}
            >
              {tarea.titulo}
            </span>
            {tarea.jira && (
              <span style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--primary-light)", background: "var(--bg-planificada)", padding: "2px 6px", borderRadius: "4px", border: "1px solid var(--border-color)", fontWeight: "500" }}>
                {tarea.jira}
              </span>
            )}
            <TaskBadge estado={est} />
            <PriorityBadge prioridad={tarea.prioridad} />
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)", display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <span><i className="ti ti-user" style={{ fontSize: "13px", marginRight: "3px" }}></i> {tarea.responsable}</span>
            {tarea.proyecto && <span><i className="ti ti-folder" style={{ fontSize: "13px", marginRight: "3px" }}></i> {tarea.proyecto}</span>}
            <span><i className="ti ti-message" style={{ fontSize: "13px", marginRight: "3px" }}></i> {tarea.avances?.length || 0} avance{(tarea.avances?.length !== 1) ? "s" : ""}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          <button 
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "6px", fontSize: "16px", borderRadius: "4px" }}
            title="Editar tarea"
            onClick={(e) => { 
              e.stopPropagation(); 
              setIsEditing(true); 
              setEditTitulo(tarea.titulo);
              setEditDescripcion(tarea.descripcion || "");
              setEditResponsable(tarea.responsable || "");
              setEditProyecto(tarea.proyecto || "");
              setEditJira(tarea.jira || "");
              setEditPrioridad(tarea.prioridad || "normal");
            }}
            onMouseOver={(e) => e.currentTarget.style.color = "var(--accent)"}
            onMouseOut={(e) => e.currentTarget.style.color = "var(--text-light)"}
          >
            <i className="ti ti-edit"></i>
          </button>
          <button 
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "6px", fontSize: "16px", borderRadius: "4px" }}
            title="Eliminar tarea"
            onClick={(e) => { e.stopPropagation(); onDelete(tarea.id); }}
            onMouseOver={(e) => e.currentTarget.style.color = "var(--color-bloqueada)"}
            onMouseOut={(e) => e.currentTarget.style.color = "var(--text-light)"}
          >
            <i className="ti ti-trash"></i>
          </button>
        </div>
      </div>

      {/* Cuerpo expandido */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--border-color)", padding: "18px", background: "#fcfdfd" }}>
          {/* Descripción de la tarea */}
          {tarea.descripcion && (
            <div style={{ marginBottom: "18px", fontSize: "13.5px", color: "var(--text-dark)", whiteSpace: "pre-line", padding: "12px 16px", background: "var(--bg-page)", borderRadius: "6px", borderLeft: "3px solid var(--accent)" }}>
              <strong style={{ display: "block", marginBottom: "4px", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--accent)" }}>Descripción:</strong>
              <div>{tarea.descripcion}</div>
            </div>
          )}
          
          {/* Avances existentes */}
          {tarea.avances?.length > 0 && (
            <div className="timeline" style={{ marginBottom: "18px" }}>
              {[...tarea.avances]
                .sort((a, b) => {
                  const dateA = a.fecha ? new Date(a.fecha) : 0;
                  const dateB = b.fecha ? new Date(b.fecha) : 0;
                  return dateB - dateA;
                })
                .map(av => {
                  const avEstObj = ESTADOS[av.estado] || ESTADOS["Planificada"];
                  const isEditingThis = editingAvanceId === av.id;
                  const isMovingThis = movingAvanceId === av.id;

                  return (
                  <div key={av.id} className="timeline-item" style={{ marginBottom: "14px" }}>
                    <div className="timeline-dot" style={{ borderColor: avEstObj.color }}></div>
                    {isEditingThis ? (
                      <div style={{ background: "var(--bg-planificada)", padding: "12px", borderRadius: "6px", border: "1px solid var(--border-color)", marginTop: "4px" }}>
                        <div style={{ display: "flex", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <div style={{ flex: "1 1 120px" }}>
                            <label style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginBottom: "2px", fontWeight: "600" }}>Autor</label>
                            <input 
                              type="text" 
                              value={editAvanceAutor} 
                              onChange={(e) => setEditAvanceAutor(e.target.value)} 
                              style={{ fontSize: "12.5px", padding: "6px 10px", width: "100%" }}
                            />
                          </div>
                          <div style={{ flex: "1 1 120px" }}>
                            <label style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginBottom: "2px", fontWeight: "600" }}>Estado</label>
                            <select 
                              value={editAvanceEstado} 
                              onChange={(e) => setEditAvanceEstado(e.target.value)} 
                              style={{ fontSize: "12.5px", padding: "6px 10px", width: "100%", height: "35px" }}
                            >
                              {Object.keys(ESTADOS).filter(k => k !== "Planificada").map(k => (
                                <option key={k} value={k}>{ESTADOS[k].label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                          <label style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginBottom: "2px", fontWeight: "600" }}>Contenido</label>
                          <textarea 
                            value={editAvanceTexto} 
                            onChange={(e) => setEditAvanceTexto(e.target.value)} 
                            rows="2"
                            style={{ fontSize: "12.5px", padding: "6px 10px", width: "100%" }}
                          />
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button 
                            className="btn-success" 
                            style={{ padding: "6px 12px", fontSize: "12.5px" }}
                            disabled={!editAvanceTexto.trim() || !editAvanceAutor.trim()}
                            onClick={async () => {
                              await onEditAvance(tarea.id, av.id, {
                                autor: editAvanceAutor.trim(),
                                estado: editAvanceEstado,
                                texto: editAvanceTexto.trim()
                              });
                              setEditingAvanceId(null);
                            }}
                          >
                            Guardar
                          </button>
                          <button 
                            style={{ padding: "6px 12px", fontSize: "12.5px" }}
                            onClick={() => setEditingAvanceId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="timeline-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span className="timeline-author">{av.autor}</span>
                            <div className="timeline-meta">
                              <TaskBadge estado={av.estado} />
                              <span>{fmtDatetime(av.fecha)}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "4px" }}>
                            <button 
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "4px", fontSize: "14px", borderRadius: "4px" }}
                              title="Editar sub-tarea"
                              onClick={() => handleStartEditAvance(av)}
                              onMouseOver={(e) => e.currentTarget.style.color = "var(--accent)"}
                              onMouseOut={(e) => e.currentTarget.style.color = "var(--text-light)"}
                            >
                              <i className="ti ti-edit"></i>
                            </button>
                            <button 
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "4px", fontSize: "14px", borderRadius: "4px" }}
                              title="Mover a otra tarea"
                              onClick={() => setMovingAvanceId(av.id)}
                              onMouseOver={(e) => e.currentTarget.style.color = "var(--accent)"}
                              onMouseOut={(e) => e.currentTarget.style.color = "var(--text-light)"}
                            >
                              <i className="ti ti-arrow-forward"></i>
                            </button>
                            <button 
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "4px", fontSize: "14px", borderRadius: "4px" }}
                              title="Eliminar sub-tarea"
                              onClick={() => onDeleteAvance(tarea.id, av.id)}
                              onMouseOver={(e) => e.currentTarget.style.color = "var(--color-bloqueada)"}
                              onMouseOut={(e) => e.currentTarget.style.color = "var(--text-light)"}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                        <div className="timeline-content" style={{ marginTop: "4px" }}>{av.texto}</div>

                        {isMovingThis && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", background: "var(--bg-planificada)", padding: "8px 12px", borderRadius: "6px", border: "1px solid var(--border-color)" }}>
                            <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: "600" }}>Mover a:</span>
                            <select 
                              defaultValue=""
                              onChange={async (e) => {
                                const destId = e.target.value;
                                if (destId) {
                                  await onMoveAvance(tarea.id, destId, av.id);
                                  setMovingAvanceId(null);
                                }
                              }}
                              style={{ fontSize: "12px", padding: "6px 10px", flex: 1, height: "32px" }}
                            >
                              <option value="" disabled>Seleccionar tarea destino...</option>
                              {tareas.filter(t => t.id !== tarea.id).map(t => (
                                <option key={t.id} value={t.id}>{t.titulo}</option>
                              ))}
                            </select>
                            <button 
                              style={{ padding: "6px 10px", fontSize: "11.5px", height: "32px", display: "inline-flex", alignItems: "center" }}
                              onClick={() => setMovingAvanceId(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Input nuevo avance */}
          {userName ? (
            <div style={{ borderTop: "1px dashed var(--border-color)", paddingTop: "16px" }}>
              <div style={{ fontSize: "12.5px", fontWeight: "600", color: "var(--text-muted)", marginBottom: "8px" }}>REGISTRAR NUEVO AVANCE</div>
              <div className="row-gap" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 240px" }}>
                  <textarea 
                    placeholder="Escribí los detalles del avance aquí..."
                    value={avanceInputs[tarea.id] || ""}
                    onChange={(e) => onInputChange(tarea.id, e.target.value)}
                    rows="2"
                    style={{ fontSize: "13.5px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "160px" }}>
                  <select 
                    value={avanceEstados[tarea.id] || "Avanzando"}
                    onChange={(e) => onEstadoChange(tarea.id, e.target.value)}
                    style={{ fontSize: "13px", padding: "8px 12px" }}
                  >
                    {Object.keys(ESTADOS).filter(k => k !== "Planificada").map(k => (
                      <option key={k} value={k}>{ESTADOS[k].label}</option>
                    ))}
                  </select>
                  <button 
                    onClick={() => onAddAvance(tarea.id)}
                    className="btn-accent"
                    style={{ justifyContent: "center", padding: "9px" }}
                    disabled={!((avanceInputs[tarea.id] || "").trim()) || saving}
                  >
                    <i className="ti ti-send"></i> Registrar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: "13px", color: "var(--text-muted)", background: "var(--bg-planificada)", padding: "10px 14px", borderRadius: "6px", border: "1px solid var(--border-color)" }}>
              <i className="ti ti-info-circle" style={{ marginRight: "6px", color: "var(--accent)" }}></i> Ingresá tu nombre arriba para cargar avances en esta tarea.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Tablero({ 
  tareas, 
  loading, 
  saving,
  userName, 
  onAddAvance, 
  onDeleteTarea, 
  onEditTarea,
  onEditAvance,
  onDeleteAvance,
  onMoveAvance,
  onToggleAddTarea, 
  showAddTarea,
  nuevaTarea,
  onNuevaTareaChange,
  onAgregarTarea,
  onToggleImport,
  showImport,
  csvText,
  onCsvTextChange,
  csvPreview,
  onConfirmarImport,
  onToggleImportDocx,
  showImportDocx,
  docxPreview,
  onConfirmarImportDocx,
  onDocxPreviewChange,
  onLoadSemana,
  onRefresh,
  avanceInputs,
  avanceEstados,
  onAvanceInputChange,
  onAvanceEstadoChange,
  expandedTask,
  onToggleExpand
}) {
  const cnt = contadores(tareas);
  const sorted = tareasSorted(tareas);

  const [selectedEstado, setSelectedEstado] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const loaderRef = useRef(null);

  // Filter tasks based on selected state
  const filtered = selectedEstado
    ? sorted.filter(t => estadoActual(t) === selectedEstado)
    : sorted;

  // Reset selected filter when tasks/month changes
  useEffect(() => {
    setSelectedEstado(null);
  }, [tareas]);

  // Reset visibleCount when tasks or selected filter changes
  useEffect(() => {
    setVisibleCount(10);
  }, [tareas, selectedEstado]);

  // Infinite scroll observer to increment visibleCount
  useEffect(() => {
    const currentLoader = loaderRef.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount(prev => Math.min(prev + 10, filtered.length));
      }
    }, {
      rootMargin: "100px" // Trigger slightly before it is fully in view
    });

    observer.observe(currentLoader);

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [filtered.length, visibleCount]);

  const totalTareas = tareas.length;

  const allPill = totalTareas > 0 ? (
    <button
      key="Todos"
      onClick={() => setSelectedEstado(null)}
      className={`counter-pill ${!selectedEstado ? "active-pill" : ""}`}
      style={{
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: "600",
        border: !selectedEstado ? "1.5px solid var(--accent)" : "1px solid var(--border-color)",
        background: !selectedEstado ? "var(--bg-card)" : "transparent",
        color: !selectedEstado ? "var(--accent)" : "var(--text-muted)",
        outline: "none",
        padding: "6px 12px",
        borderRadius: "6px"
      }}
      title="Mostrar todas las tareas"
    >
      <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: !selectedEstado ? "var(--accent)" : "var(--text-muted)" }}></span>
      {totalTareas} Todos
    </button>
  ) : null;

  const pills = Object.entries(cnt)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => {
      const e = ESTADOS[k] || ESTADOS["Planificada"];
      const isSelected = selectedEstado === k;
      return (
        <button
          key={k}
          onClick={() => setSelectedEstado(isSelected ? null : k)}
          className={`counter-pill ${isSelected ? "active-pill" : ""}`}
          style={{
            cursor: "pointer",
            borderColor: isSelected ? e.color : `${e.color}33`,
            borderWidth: isSelected ? "2px" : "1.5px",
            borderStyle: "solid",
            color: isSelected ? e.color : `${e.color}cc`,
            background: isSelected ? `${e.bg}` : "transparent",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: "600",
            transition: "all 0.2s ease",
            outline: "none",
            padding: "6px 12px",
            borderRadius: "6px"
          }}
          title={isSelected ? `Quitar filtro: ${e.label}` : `Filtrar por: ${e.label}`}
        >
          <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: e.color }}></span>
          {v} {e.label}
        </button>
      );
    });

  return (
    <div>
      {/* Contadores */}
      <div className="counters-bar" style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
        {allPill}
        {pills.length ? pills : (!allPill && <div style={{ fontSize: "13.5px", color: "var(--text-light)" }}>Sin tareas registradas esta semana</div>)}
      </div>

      {/* Botones acción */}
      <div className="row-gap" style={{ marginBottom: "20px", justifyContent: "space-between" }}>
        <div className="row-gap">
          <button onClick={() => onToggleAddTarea()} className="btn-accent">
            <i className="ti ti-plus"></i> Nueva tarea
          </button>
          <button onClick={() => onToggleImport()}>
            <i className="ti ti-table-import"></i> Importar CSV (Jira)
          </button>
          <button onClick={() => onToggleImportDocx()} className="btn-info">
            <i className="ti ti-file-text"></i> Importar DOCX
          </button>
          <button onClick={onRefresh} className="btn-info">
            <i className="ti ti-refresh"></i> Actualizar
          </button>
        </div>
        {saving && (
          <span style={{ fontSize: "13px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "6px" }}>
            <span className="spinner"></span> Sincronizando...
          </span>
        )}
      </div>

      {/* Form nueva tarea */}
      {showAddTarea && (
        <FormNuevaTarea 
          nuevaTarea={nuevaTarea}
          onNuevaTareaChange={onNuevaTareaChange}
          onAgregarTarea={onAgregarTarea}
          onToggleAddTarea={onToggleAddTarea}
        />
      )}

      {/* Import CSV */}
      {showImport && (
        <FormImportCSV 
          csvText={csvText}
          onCsvTextChange={onCsvTextChange}
          csvPreview={csvPreview}
          onConfirmarImport={onConfirmarImport}
          onToggleImport={onToggleImport}
        />
      )}

      {/* Import DOCX */}
      {showImportDocx && (
        <FormImportDocx 
          docxPreview={docxPreview}
          onDocxPreviewChange={onDocxPreviewChange}
          onConfirmarImportDocx={onConfirmarImportDocx}
          onToggleImportDocx={onToggleImportDocx}
          userName={userName}
        />
      )}

      {/* Lista de tareas */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px", color: "var(--text-muted)" }}>
          <span className="spinner" style={{ width: "24px", height: "24px", borderWidth: "3px" }}></span>
          <div style={{ marginTop: "12px", fontSize: "14px" }}>Cargando tareas judiciales...</div>
        </div>
      ) : sorted.length === 0 ? (
        <div className="empty-state">
          <i className="ti ti-scale" style={{ fontSize: "48px", color: "var(--accent)" }}></i>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-dark)" }}>No hay actividades registradas</div>
          <p style={{ fontSize: "13.5px", color: "var(--text-muted)", marginTop: "6px" }}>Utilizá el botón "Nueva tarea" o importá actividades desde un archivo CSV de Jira.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <i className="ti ti-filter-off" style={{ fontSize: "48px", color: "var(--accent)" }}></i>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-dark)" }}>No hay tareas con el estado "{selectedEstado}"</div>
          <p style={{ fontSize: "13.5px", color: "var(--text-muted)", marginTop: "6px" }}>Probá seleccionando otro filtro o limpiando el filtro actual.</p>
          <button onClick={() => setSelectedEstado(null)} className="btn-primary" style={{ marginTop: "12px" }}>
            Ver todas las tareas
          </button>
        </div>
      ) : (
        <div className="task-list">
          {filtered.slice(0, visibleCount).map(tarea => (
            <TaskCard 
              key={tarea.id}
              tarea={tarea}
              expanded={expandedTask === tarea.id}
              onToggleExpand={onToggleExpand}
              onDelete={onDeleteTarea}
              userName={userName}
              onAddAvance={onAddAvance}
              avanceInputs={avanceInputs}
              avanceEstados={avanceEstados}
              onInputChange={onAvanceInputChange}
              onEstadoChange={onAvanceEstadoChange}
              saving={saving}
              onEdit={onEditTarea}
              tareas={tareas}
              onEditAvance={onEditAvance}
              onDeleteAvance={onDeleteAvance}
              onMoveAvance={onMoveAvance}
            />
          ))}
          {visibleCount < filtered.length && (
            <div 
              ref={loaderRef} 
              style={{ 
                padding: "20px", 
                textAlign: "center", 
                color: "var(--text-muted)", 
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                marginTop: "10px",
                borderTop: "1px dashed var(--border-color)"
              }}
            >
              <span className="spinner" style={{ width: "16px", height: "16px" }}></span> 
              Cargando más tareas...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormNuevaTarea({ nuevaTarea, onNuevaTareaChange, onAgregarTarea, onToggleAddTarea }) {
  const [errors, setErrors] = useState({});
  const canAdd = nuevaTarea.titulo.trim() && nuevaTarea.responsable.trim();

  const handleAgregar = () => {
    const errs = {};
    if (!nuevaTarea.titulo.trim()) errs.titulo = true;
    if (!nuevaTarea.responsable.trim()) errs.responsable = true;
    
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onAgregarTarea();
  };

  return (
    <div className="form-panel">
      <div className="form-title">Registrar Nueva Tarea</div>
      <div className="grid2">
        <div>
          <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Título de la Tarea *</label>
          <input 
            type="text"
            placeholder="Ej: Migración de base de datos de expedientes"
            value={nuevaTarea.titulo}
            onChange={(e) => {
              onNuevaTareaChange("titulo", e.target.value);
              if (errors.titulo) setErrors({ ...errors, titulo: false });
            }}
            style={{ borderColor: errors.titulo ? "var(--color-bloqueada)" : "" }}
          />
        </div>
        <div>
          <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Responsable *</label>
          <input 
            type="text"
            placeholder="Nombre y apellido"
            value={nuevaTarea.responsable}
            onChange={(e) => {
              onNuevaTareaChange("responsable", e.target.value);
              if (errors.responsable) setErrors({ ...errors, responsable: false });
            }}
            style={{ borderColor: errors.responsable ? "var(--color-bloqueada)" : "" }}
          />
        </div>
        <div>
          <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Proyecto</label>
          <input 
            type="text"
            placeholder="Ej: Mesa de Entradas Virtual"
            value={nuevaTarea.proyecto}
            onChange={(e) => onNuevaTareaChange("proyecto", e.target.value)}
          />
        </div>
        <div>
          <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Clave Jira (opcional)</label>
          <input 
            type="text"
            placeholder="Ej: DGIT-345"
            value={nuevaTarea.jira}
            onChange={(e) => onNuevaTareaChange("jira", e.target.value)}
          />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Descripción (opcional)</label>
          <textarea 
            placeholder="Detalles sobre los objetivos y alcance de la tarea..."
            value={nuevaTarea.descripcion || ""}
            onChange={(e) => onNuevaTareaChange("descripcion", e.target.value)}
            rows="2"
          />
        </div>
        <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
          <span style={{ fontSize: "13.5px", color: "var(--text-muted)" }}>Prioridad:</span>
          <select 
            value={nuevaTarea.prioridad || "normal"}
            onChange={(e) => onNuevaTareaChange("prioridad", e.target.value)}
            style={{ width: "auto", padding: "8px 12px" }}
          >
            <option value="critica">Crítica</option>
            <option value="alta">Alta</option>
            <option value="normal">Normal</option>
            <option value="baja">Baja</option>
          </select>
        </div>
      </div>
      <div className="row-gap" style={{ marginTop: "16px" }}>
        <button onClick={handleAgregar} className="btn-accent" disabled={!canAdd}>
          <i className="ti ti-circle-check"></i> Registrar tarea
        </button>
        <button onClick={() => onToggleAddTarea()}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function FormImportCSV({ csvText, onCsvTextChange, csvPreview, onConfirmarImport, onToggleImport }) {
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = ev => {
      onCsvTextChange(ev.target.result);
    };
    r.readAsText(f);
  };

  return (
    <div className="form-panel">
      <div className="form-title">Importar Tareas desde Jira (CSV)</div>
      <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "12px" }}>
        Cargá un archivo CSV exportado desde Jira o pegá directamente el contenido en el área de texto. El sistema detectará las columnas correspondientes.
      </p>
      <div className="row-gap" style={{ marginBottom: "14px" }}>
        <input 
          type="file" 
          ref={fileInputRef}
          accept=".csv,text/csv"
          onChange={handleFileChange}
          style={{ fontSize: "13px", width: "auto", padding: "6px 12px" }}
        />
      </div>
      <textarea 
        placeholder="Pegá el contenido CSV de Jira aquí..."
        value={csvText}
        onChange={(e) => onCsvTextChange(e.target.value)}
        rows="4"
        style={{ fontSize: "13px", fontFamily: "monospace" }}
      />
      {csvPreview ? (
        <div style={{ marginTop: "14px" }}>
          <div style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--text-dark)", marginBottom: "8px" }}>
            Se detectaron {csvPreview.length} tareas válidas:
          </div>
          <div style={{ maxHeight: "160px", overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: "6px", background: "#ffffff" }}>
            {csvPreview.map((t, idx) => (
              <div key={t.id || idx} style={{ padding: "8px 12px", borderBottom: "1px solid var(--border-color)", fontSize: "13px", display: "flex", gap: "12px", alignItems: "center" }}>
                {t.jira && <span style={{ color: "var(--primary-light)", fontFamily: "monospace", fontSize: "11.5px", background: "var(--bg-planificada)", padding: "1px 5px", borderRadius: "3px" }}>{t.jira}</span>}
                <span style={{ flex: 1, fontWeight: "500", color: "var(--text-dark)" }}>{t.titulo}</span>
                {t.responsable && <span style={{ color: "var(--text-muted)", fontSize: "12px" }}><i className="ti ti-user"></i> {t.responsable}</span>}
              </div>
            ))}
          </div>
          <div className="row-gap" style={{ marginTop: "14px" }}>
            <button onClick={onConfirmarImport} className="btn-success">
              <i className="ti ti-check"></i> Importar actividades
            </button>
            <button onClick={() => onToggleImport()}>
              Cancelar
            </button>
          </div>
        </div>
      ) : csvText ? (
        <div style={{ fontSize: "13px", color: "var(--color-bloqueada)", marginTop: "10px", background: "var(--bg-bloqueada)", padding: "8px 12px", borderRadius: "6px", border: "1px solid rgba(220, 38, 38, 0.15)" }}>
          <i className="ti ti-alert-triangle"></i> No se detectaron columnas válidas (se requiere al menos "Summary"/"Resumen" o "Issue Key"/"Clave").
        </div>
      ) : null}
    </div>
  );
}

function FormImportDocx({ docxPreview, onDocxPreviewChange, onConfirmarImportDocx, onToggleImportDocx, userName }) {
  const fileInputRef = React.useRef(null);
  const [parsingError, setParsingError] = useState(null);
  const [parsing, setParsing] = useState(false);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setParsing(true);
    setParsingError(null);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const arrayBuffer = ev.target.result;
          const tasks = await parseDocx(arrayBuffer, userName);
          if (tasks.length === 0) {
            setParsingError("No se encontraron tareas con código de Jira en el documento.");
            onDocxPreviewChange(null);
          } else {
            onDocxPreviewChange(tasks);
          }
        } catch (err) {
          setParsingError("Error al procesar el archivo: " + err.message);
          onDocxPreviewChange(null);
        } finally {
          setParsing(false);
        }
      };
      reader.readAsArrayBuffer(f);
    } catch (err) {
      setParsingError("Error al leer el archivo: " + err.message);
      onDocxPreviewChange(null);
      setParsing(false);
    }
  };

  return (
    <div className="form-panel">
      <div className="form-title">Importar Tareas desde Documento de Word (DOCX)</div>
      <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "12px" }}>
        Cargá un archivo de Word (`.docx`). El sistema extraerá como tareas todos los títulos que contengan un número de tarjeta de Jira (ej: "Juicio por jurado IA-290"), y como avances todos los bullet points ubicados inmediatamente por debajo.
      </p>
      <div className="row-gap" style={{ marginBottom: "14px" }}>
        <input 
          type="file" 
          ref={fileInputRef}
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
          style={{ fontSize: "13px", width: "auto", padding: "6px 12px" }}
        />
        {parsing && <span style={{ fontSize: "13px", color: "var(--text-muted)" }}><span className="spinner"></span> Procesando documento...</span>}
      </div>

      {parsingError && (
        <div style={{ fontSize: "13px", color: "var(--color-bloqueada)", marginTop: "10px", background: "var(--bg-bloqueada)", padding: "8px 12px", borderRadius: "6px", border: "1px solid rgba(220, 38, 38, 0.15)" }}>
          <i className="ti ti-alert-triangle"></i> {parsingError}
        </div>
      )}

      {docxPreview && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--text-dark)", marginBottom: "8px" }}>
            Se detectaron {docxPreview.length} tareas con sus respectivos avances:
          </div>
          <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: "6px", background: "#ffffff", padding: "8px" }}>
            {docxPreview.map((t, idx) => (
              <div key={t.id || idx} style={{ padding: "8px", borderBottom: idx < docxPreview.length - 1 ? "1px solid var(--border-color)" : "none", fontSize: "13.5px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", fontWeight: "600" }}>
                  {t.jira && <span style={{ color: "var(--primary-light)", fontFamily: "monospace", fontSize: "11px", background: "var(--bg-planificada)", padding: "1px 5px", borderRadius: "3px" }}>{t.jira}</span>}
                  <span style={{ color: "var(--text-dark)" }}>{t.titulo}</span>
                </div>
                {t.avances && t.avances.length > 0 && (
                  <div style={{ paddingLeft: "20px", marginTop: "4px", display: "flex", flexDirection: "column", gap: "2px" }}>
                    {t.avances.map((av, avIdx) => (
                      <div key={av.id || avIdx} style={{ color: "var(--text-muted)", fontSize: "12.5px" }}>
                        • {av.texto}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="row-gap" style={{ marginTop: "14px" }}>
            <button onClick={onConfirmarImportDocx} className="btn-success">
              <i className="ti ti-check"></i> Confirmar importación
            </button>
            <button onClick={() => onToggleImportDocx()}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
